import { QueryClient } from "@tanstack/react-query";
import {
    addRxPlugin,
    createRxDatabase
} from 'rxdb/plugins/core';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { Knot, NodeSummary, SubscriptionStatus } from "./query";
import { generateKnots, generateNodes } from "./utils";

if (import.meta.env.MODE !== 'production') {
    addRxPlugin(RxDBDevModePlugin);
}

export const queryClient = new QueryClient()

const nodeSchema = {
    version: 0,
    primaryKey: 'id',
    type: 'object',
    properties: {
        id: {
            type: 'string',
            maxLength: 100 // <- the primary key must have set maxLength
        },
        circleId: {
            type: 'string',
            maxLength: 100 
        },
        name: {
            type: 'string'
        },
        type: {
            type: 'string'
        },
        subtitle: {
            type: 'string'
        },
        description: {
            type: 'string'
        },
        avatars: {
            type: "array",
            items: {
                type: "string"
            }
        },
        tags: {
            type: "array",
            items: {
                type: "string"
            }
        },
        timestamp: {
            type: 'string',
            format: 'date-time'
        }
    },
    required: ['id', 'name']
}


const knotSchema = {
    version: 0,
    primaryKey: 'id',
    type: 'object',
    properties: {
        id: {
            type: 'string',
            maxLength: 100 // <- the primary key must have set maxLength
        },
        circleId: {
            type: 'string',
            maxLength: 100 
        },
        nodeId: {
            type: 'string',
            maxLength: 100 // <- the primary key must have set maxLength
        },
        type: {
            type: 'string'
        },
        title: {
            type: 'string'
        },
        description: {
            type: 'string'
        },
        tags: {
            type: "array",
            items: {
                type: "string"
            }
        },
        timestamp: {
            type: 'string',
            format: 'date-time'
        }
    },
    required: ['id', 'name']
}


// Top-level await is not supported in some environments
export const db = await createRxDatabase({
    name: 'arcradius', // the name of the database
    storage: getRxStorageDexie()
});

const setup = async () => {
    await db.addCollections({
        nodes: {
            schema: nodeSchema
        },
        knots: {
            schema: knotSchema
        }
    });

    console.log("Done")
}
//TODO: Mark as setup Database
setup();

export const getSubscriptionStatus = async (_token?: string): Promise<SubscriptionStatus> => {
    return {
        status: 'active'
    };
}

export const addKnot = async (newKnote: Knot, _token?: string): Promise<void> => {
    await db.knots.insert(newKnote)
    return;
}

export const addNode = async (newNode: NodeSummary, _token?: string): Promise<void> => {
    await db.nodes.insert(newNode)
    return;
}

export const deleteNode = async (nodeId: string, _token?: string): Promise<void> => {
    const node = await db.nodes.findOne(nodeId).exec();
    if (node) {
        await node.remove();
    }
}

export const getNode = async (nodeId: string, _token?: string): Promise<NodeSummary | null> => {
    const node = await db.nodes.findOne(nodeId).exec();
    return node.toJSON();
}

export const editNode = async (nodeId: string, newNode: NodeSummary, _token?: string): Promise<void> => {
    const node = await db.nodes.findOne(nodeId).exec();

    //TODO: Check update
    if (node) {
        await node.patch(newNode);
    }
}
export const mergeNodes = async (nodes: string[], _token?: string): Promise<void> => {
    console.log("Merging", nodes)
    // Get the first node
    const firstNode = nodes[0];
    // Get the rest of the nodes
    const restNodes = nodes.slice(1);

    //Migrate all kots to the first node
    for (const nodeId of restNodes) {
        const selector = {
            nodeId: nodeId
        };
        const queryOptions: any = {
            selector
        };
        const knots = await db.knots.find(queryOptions).exec();
        console.log("Knots", knots)
        for (const knot of knots) {
            await knot.patch({
                nodeId: firstNode
            });
        }
    }
    console.log("Migrated Knots")
    //TODO: Check the fields

    // Remove the rest of the nodes
    for (const nodeId of restNodes) {
        console.log("Deleting", nodeId)
        await deleteNode(nodeId);
    }
}



export type ApiResponse<T> = {
    data: T[];
    nextCursor: number | undefined;
}
//TODO: Rename to getNodes
export const getPersons = async (searchParam: string, _token?: string, pageParam: number = 1): Promise<ApiResponse<NodeSummary>> => {
    const limit = 50;
    const skip = (pageParam - 1) * limit;
    const selector = searchParam
        ? {
            name: { $regex: `.*${searchParam}.*`, $options: 'i' }
        }
        : {};

    const queryOptions: any = {
        selector,
        limit,
        skip,
        sort: [{ id: 'asc' }]
    };

    const persons = await db.nodes.find(queryOptions).exec();
    const nextCursor = persons.length === limit ? pageParam + 1 : undefined;

    return { data: persons, nextCursor };
}

export const getKnots = async (nodeId: string, _token: string, searchParam: string, pageParam: number = 1): Promise<ApiResponse<Knot>> => {
    const limit = 50;
    const skip = (pageParam - 1) * limit;
    const selector = searchParam
        ? {
            nodeId: nodeId,
            title: { $regex: `.*${searchParam}.*`, $options: 'i' }
        }
        : {
            nodeId: nodeId
        };

    const queryOptions: any = {
        selector,
        limit,
        skip,
        sort: [{ id: 'asc' }]
    };

    const knots = await db.knots.find(queryOptions).exec();
    const nextCursor = knots.length === limit ? pageParam + 1 : undefined;

    return { data: knots, nextCursor };
}

export const removeAllLocalData = async () => {
    await db.remove(); // or db.destroy()
};
